<template>
    <div>
        <section id="heading" class="py-12">
            <!-- Header Nav -->
            <header-nav></header-nav>
        </section>
        <section class="py-12">
            <div class="max-w-sm mx-auto px-6 mb-6" v-if="errorText">
                <div class="bg-red-light w-full shadow border border-red-light text-white px-4 py-3 rounded relative">
                    <span class="icon mr-1 text-lg"><i class="mdi mdi-alert-circle-outline"></i></span>
                    <span class="font-semibold" v-text="errorText"></span>
                </div>
            </div>
            <patient-loading v-if="loadingAppointments || loadingInvitations || loadingEmergencies || loadingPartnerAppointments"></patient-loading>

            <div class="max-w-sm mx-auto px-6" v-if="! loadingInvitations && Object.keys(invitations).length && patient">
                <div class="uppercase tracking-wide text-grey-darker text-sm font-semibold mb-6" v-text="$t('Invitation_from_dentist')"></div>
                <invitation-list-item v-for="invitation in sortedInvitations" :key="invitation.id" :invitation="invitation"></invitation-list-item>
            </div>

            <div class="max-w-sm mx-auto px-6" v-if="! loadingEmergencies && Object.keys(emergencies).length && patient">
                <div class="uppercase tracking-wide text-grey-darker text-sm font-semibold mb-6" v-text="$t('Pending')"></div>
                <emergency-list-item v-for="emergency in sortedEmergencies" :key="emergency.id" :emergency="emergency" @deleted="fetchEmergencies"></emergency-list-item>
            </div>

            <div class="max-w-sm mx-auto px-6" v-if="!loadingAppointments && (Object.keys(appointments).length || Object.keys(partnerAppointments).length) && patient">
                <div class="uppercase tracking-wide text-grey-darker text-sm font-semibold mb-6" v-text="$t('Your_confirmed_appointments')"></div>

                <appointment-list-item v-for="appointment in sortedAppointments" :key="appointment.id" :appointment="appointment"></appointment-list-item>
                <partner-appointment-list-item v-for="appointment in sortedPartnerAppointments" :key="appointment.id" :appointment="appointment"></partner-appointment-list-item>
            </div>

            <div class="max-w-sm mx-auto px-6">
                <router-link :to="{name: 'search'}" class="inline-block w-full px-4 py-3 rounded bg-blue hover:bg-blue-dark text-sm text-white uppercase tracking-wide font-semibold text-center">
                    {{ $t('Find_an_appointment') }}
                </router-link>
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios'
import firebase from 'firebase/app'
import helpers from '@/mixins/helpers'
import moment from 'moment'

import HeaderNav from '@/components/Patient/HeaderNav'
import AppointmentListItem from '@/components/Patient/AppointmentListItem'
import PartnerAppointmentListItem from '@/components/Patient/PartnerAppointmentListItem'
import InvitationListItem from '@/components/Patient/InvitationListItem'
import EmergencyListItem from '@/components/Patient/EmergencyListItem'

import appointmentRepository from '@/data/AppointmentRepository'
import officeRepository from '@/data/OfficeRepository'
import appointmentInvitationRepository from '@/data/AppointmentInvitationRepository'
import emergencyRepository from '@/data/EmergencyRepository'

export default {
    name: 'Home',

    mixins: [helpers],

    components: {HeaderNav, AppointmentListItem, PartnerAppointmentListItem, InvitationListItem, EmergencyListItem},

    data () {
        return {
            errorText: null,
            loadingOffices: false,
            loadingAppointments: false,
            loadingPartnerAppointments: false,
            loadingInvitations: false,
            loadingEmergencies: false,
            appointments: [],
            officesData: [],
            invitations: [],
            emergencies: [],
            partnerAppointments: []
        }
    },

    computed: {
        sortedAppointments () {
            let appointmentsAndOffices = []
            const entries = Object.entries(this.appointments).forEach(([id, appointment]) => {
                this.officesData && this.officesData.length > 0 && this.officesData.forEach(office => {
                    if (office.id === appointment.officeId) {
                        appointmentsAndOffices.push({
                            ...office,
                            ...appointment
                        })
                    }
                })
            })

            return _.sortBy(appointmentsAndOffices, ['date', 'time'])
        },
        sortedPartnerAppointments () {
            let now = moment()
            return _.sortBy(this.partnerAppointments.filter(appointment => {
                return now.diff(moment(appointment.dateTime)) < 0
            }), ['dateTime'])
        },
        sortedInvitations () {
            return _.sortBy(this.invitations, ['date', 'time'])
        },
        sortedEmergencies () {
            return _.sortBy(this.emergencies, ['date', 'time'])
        }
    },

    mounted () {
        // if (this.userId) {
        //     this.fetchAppointments()
        //     this.fetchPartnerAppointments()
        //     this.fetchInvitations()
        //     this.fetchEmergencies()
        // } else {
        //     this.$router.push("/search")
        // }

        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                this.fetchOffices()
                this.fetchAppointments()
                this.fetchPartnerAppointments()
                this.fetchInvitations()
                this.fetchEmergencies()
            } else {
                this.invitations = []
                this.appointments = []
                this.partnerAppointments = []
                this.emergencies = []
            }
        })
    },

    methods: {
        fetchOffices () {
            this.loadingOffices = true
            officeRepository.getAll((offices) => {
                this.officesData = offices
                this.loadingOffices = false
            }, (error) => {
                this.errorText = this.$t('appointments_could_not_be_loaded')
                this.loadingOffices = false
            })
        },

        fetchAppointments () {
            if (! this.userId) {
                return false
            }

            this.loadingAppointments = true

            appointmentRepository.allForPatient(this.userId, (appointments) => {
                this.appointments = appointments

                this.loadingAppointments = false
            }, (error) => {
                this.errorText = this.$t('appointments_could_not_be_loaded')
                this.loadingAppointments = false
            })
        },

        fetchPartnerAppointments () {
            if (! this.userId) {
                return false
            }

            this.loadingPartnerAppointments = true

            axios.get(process.env.CLOUD_FUNCTIONS_BASE_URI + '/getPartnerAppointments', {
                params: {
                    userId: this.userId,
                    language: this.patientLanguage.shortCode
                },
                headers: {
                    'Content-Type':  'application/json',
                }
            })
            .then((response) => {
                this.partnerAppointments = response.data

                this.loadingPartnerAppointments = false
            })
            .catch(error => {
                // this.errorText = this.$t('Errors.partner_appointment_invitations_could_not_be_loaded')
                this.loadingPartnerAppointments = false
            })
        },

        fetchInvitations () {
            if (! this.userId) {
                return false
            }

            this.loadingInvitations = true

            appointmentInvitationRepository.allForPatient(this.userId, (invitations) => {
                this.invitations = invitations

                this.loadingInvitations = false
            }, (error) => {
                this.errorText = this.$t('appointment_invitations_could_not_be_loaded')

                this.loadingInvitations = false
            })
        },

        fetchEmergencies () {
            if (! this.userId) {
                return false
            }

            this.loadingEmergencies = true

            emergencyRepository.allForPatient(this.userId, (emergencies) => {
                this.emergencies = emergencies

                this.loadingEmergencies = false
            }, (error) => {
                this.errorText = this.$t('emergencies_could_not_be_loaded')

                this.loadingEmergencies = false
            })
        },

        redirectIfEmpty () {
            if (Object.keys(this.invitations).length === 0 && Object.keys(this.appointments).length === 0 && Object.keys(this.emergencies).length === 0 && Object.keys(this.partnerAppointments).length === 0) {
                this.$router.push('/search')
            }
        }
    },

    watch: {
        loadingAppointments (newValue, oldValue) {
            // Redirect to search if there is no appointements nor invitations nor emergencies
            if (newValue == false && this.loadingInvitations == false && this.loadingEmergencies == false && this.loadingPartnerAppointments == false) {
                this.redirectIfEmpty()
            }
        },
        loadingInvitations (newValue, oldValue) {
            // Redirect to search if there is no appointements nor invitations nor emergencies
            if (newValue == false && this.loadingAppointments == false && this.loadingEmergencies == false && this.loadingPartnerAppointments == false) {
                this.redirectIfEmpty()
            }
        },
        loadingEmergencies (newValue, oldValue) {
            // Redirect to search if there is no appointements nor invitations nor emergencies
            if (newValue == false && this.loadingAppointments == false && this.loadingInvitations == false && this.loadingPartnerAppointments == false) {
                this.redirectIfEmpty()
            }
        },
        loadingPartnerAppointments (newValue, oldValue) {
            // Redirect to search if there is no appointements nor invitations nor emergencies
            if (newValue == false && this.loadingAppointments == false && this.loadingInvitations == false && this.loadingEmergencies == false) {
                this.redirectIfEmpty()
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Home.vue